import { v4 as uuid } from 'uuid';
import {
	type ApiGetWorkflowResponse,
	type MetaWorkflowDocument,
	type ApiAssociation,
	type ApiConditionGroup,
	type ApiGetWorkflowResponseWithUsages,
	type ApiGetWorkflowVersionResponse,
	type MetaWorkflowVersionDocument,
	type ApiRule,
	type ApiTransition,
	type ApiStatusResponse,
	type ApiWorkflowVersion,
	type ApiWorkflow,
	ACTION_RULE_TYPE,
	CONDITION_RULE_TYPE,
	type ConditionGroup,
	type MetaWorkflowDocumentWithUsages,
	type Rule,
	SCREEN_RULE_TYPE,
	TRIGGER_RULE_TYPE,
	VALIDATOR_RULE_TYPE,
	type Transition,
	type Status,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import {
	START_NODE_ID,
	transitionType,
} from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import { TODO } from '@jira/platform__status-categories/src/status-categories.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type IssueTypeId = string;

type StatusId = string;

const flattenConditionsRecursively = (conditionGroup?: ApiConditionGroup): ApiRule[] => {
	if (!conditionGroup) {
		return [];
	}

	return conditionGroup.conditions.concat(
		conditionGroup.conditionGroups.flatMap(flattenConditionsRecursively),
	);
};

const transformConditionGroupRecursively = (conditionGroup: ApiConditionGroup): ConditionGroup => ({
	id: uuid(),
	operation: conditionGroup.operation,
	conditionGroups: conditionGroup.conditionGroups.map(transformConditionGroupRecursively),
	conditionIds: conditionGroup.conditions.map(({ id }) => id),
});

const transformRule = (rule: ApiRule): Rule => ({
	id: rule.id,
	displayText: '',
	ruleKey: rule.ruleKey,
	config: rule.parameters,
	...(rule.type ? { type: rule.type } : {}),
});

const transformRules = (transition: ApiTransition) => {
	const actions = (transition.actions || []).map((action) => ({
		...action,
		type: ACTION_RULE_TYPE,
	}));
	const conditions = flattenConditionsRecursively(transition.conditions).map((condition) => ({
		...condition,
		type: CONDITION_RULE_TYPE,
	}));
	const validators = (transition.validators || []).map((validator) => ({
		...validator,
		type: VALIDATOR_RULE_TYPE,
	}));
	const triggers = (transition.triggers || []).map((trigger) => ({
		...trigger,
		type: TRIGGER_RULE_TYPE,
	}));
	const screens = transition.transitionScreen
		? [{ ...transition.transitionScreen, type: SCREEN_RULE_TYPE }]
		: [];

	const rules = [...actions, ...conditions, ...validators, ...triggers, ...screens];

	let conditionTree: ConditionGroup | undefined;

	if (transition.conditions) {
		conditionTree = transformConditionGroupRecursively(transition.conditions);
	}

	return {
		rules: rules.map(transformRule),
		conditionTree,
	};
};

const getTransitionLinks = (transition: ApiTransition): Transition['links'] => {
	const { links } = transition;

	if (transition.type === transitionType.INITIAL) {
		return [
			{
				fromRef: START_NODE_ID,
				fromPort: 0,
				toPort: links[0]?.toPort || 0,
			},
		];
	}

	if (transition.type === transitionType.GLOBAL) {
		return [];
	}

	return links.reduce<Transition['links']>((acc, link) => {
		if (link.fromStatusReference !== undefined) {
			acc.push({
				fromRef: link.fromStatusReference,
				fromPort: link.fromPort,
				toPort: link.toPort,
			});
		}
		return acc;
	}, []);
};

const transformTransitions = (transitions: ApiTransition[]) => {
	const transformed: Record<string, Transition> = {};

	transitions.forEach((transition) => {
		transformed[transition.id] = {
			id: transition.id,
			type: transitionType[transition.type],
			name: transition.name,
			description: transition.description || '',
			toRef: transition.toStatusReference,
			links: getTransitionLinks(transition),
			properties: transition.properties ?? {},
			...transformRules(transition),
			...(transition.customIssueEventId
				? { customIssueEventId: transition.customIssueEventId }
				: {}),
		};
	});
	return transformed;
};

const transformStatuses = (
	responseStatuses: ApiStatusResponse[],
	workflow: ApiWorkflow | ApiWorkflowVersion,
) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const allStatuses: Record<string, any> = {};
	const visibleStatuses: Array<{ ref: StatusId }> = [];
	const missingStatusIdToPlaceholderId: Record<string, string> = {};

	responseStatuses.forEach((status) => {
		const workflowStatus = workflow.statuses.find(
			(candidate) => candidate.statusReference === status.id,
		);

		allStatuses[status.statusReference] = {
			id: status.id,
			name: status.name,
			description: status.description,
			isNew: false,
			statusCategory: status.statusCategory,
			scope: status.scope.type,
			layout:
				workflowStatus?.layout && workflowStatus.layout.x && workflowStatus.layout.y
					? workflowStatus.layout
					: undefined,
			properties: workflowStatus?.properties ?? {},
			...(workflowStatus?.approvalConfiguration && {
				approvalConfiguration: workflowStatus.approvalConfiguration,
			}),
		};
	});

	workflow.statuses.forEach((workflowStatus) => {
		if (allStatuses[workflowStatus.statusReference]) {
			visibleStatuses.push({ ref: workflowStatus.statusReference });
		} else if (fg('wfe_handle_missing_statuses')) {
			const placeholderStatus: Status = {
				id: uuid(),
				name: '',
				description: '',
				statusCategory: TODO,
				scope: workflow.scope.type,
				isNew: true,
				layout:
					workflowStatus?.layout && workflowStatus.layout.x && workflowStatus.layout.y
						? { x: workflowStatus.layout.x, y: workflowStatus.layout.y }
						: undefined,
				properties: workflowStatus?.properties ?? {},
				...(workflowStatus?.approvalConfiguration && {
					approvalConfiguration: workflowStatus.approvalConfiguration,
				}),
				isMissingStatusPlaceholder: true,
			};

			missingStatusIdToPlaceholderId[workflowStatus.statusReference] = placeholderStatus.id;
			allStatuses[placeholderStatus.id] = placeholderStatus;
			visibleStatuses.push({ ref: placeholderStatus.id });
		}
	});

	return { allStatuses, visibleStatuses, missingStatusIdToPlaceholderId };
};

export const transform = (response: ApiGetWorkflowResponse): MetaWorkflowDocument => {
	const [workflow] = response.workflows;

	const { allStatuses, visibleStatuses, missingStatusIdToPlaceholderId } = transformStatuses(
		response.statuses,
		workflow,
	);

	let remappedTransitions: ApiTransition[] = [];
	if (fg('wfe_handle_missing_statuses')) {
		remappedTransitions = workflow.transitions.map((transition) => {
			const mapMissingStatusRef = (statusReference: string | undefined) => {
				if (statusReference && missingStatusIdToPlaceholderId[statusReference]) {
					return missingStatusIdToPlaceholderId[statusReference];
				}
				return statusReference;
			};

			return {
				...transition,
				toStatusReference: mapMissingStatusRef(transition.toStatusReference),
				links: transition.links.map((link) => ({
					...link,
					fromStatusReference: mapMissingStatusRef(link.fromStatusReference),
				})),
			};
		});
	}

	return {
		id: workflow.id,
		name: workflow.name,
		description: workflow.description,
		versionNumber: workflow.version.versionNumber,
		versionId: workflow.version.id,
		scope: workflow.scope,
		allStatuses,
		visibleStatuses,
		transitions: transformTransitions(
			fg('wfe_handle_missing_statuses') ? remappedTransitions : workflow.transitions,
		),
		startNodeLayout: workflow.startPointLayout || {},
		loopedTransitionContainerLayout: workflow.loopedTransitionContainerLayout || {},
		migrationId: workflow.migrationId,
		isEditable: workflow.isEditable,
		hasDraft: workflow.hasDraft,
	};
};

export const transformWorkflowVersion = (
	response: ApiGetWorkflowVersionResponse,
): MetaWorkflowVersionDocument => {
	const [workflow] = response.workflows;
	const { allStatuses, visibleStatuses } = transformStatuses(response.statuses, workflow);

	return {
		id: workflow.id,
		name: workflow.name,
		description: workflow.description,
		versionNumber: workflow.version.versionNumber,
		versionId: workflow.version.id,
		scope: workflow.scope,
		allStatuses,
		visibleStatuses,
		transitions: transformTransitions(workflow.transitions),
		startNodeLayout: workflow.startPointLayout || {},
		loopedTransitionContainerLayout: workflow.loopedTransitionContainerLayout || {},
	};
};

export const transformWithUsages = (
	response: ApiGetWorkflowResponseWithUsages,
): MetaWorkflowDocumentWithUsages => {
	const [workflow] = response.workflows;
	const transformedWorkflow = transform(response);
	const getAssociatedIssueTypes = (associations?: ApiAssociation[]): IssueTypeId[] =>
		associations?.length ? associations[0].issueTypes.map((id) => id) : [];

	return {
		...transformedWorkflow,
		associatedIssueTypeIds: getAssociatedIssueTypes(workflow.associatedWith),
		associatedEntityIds: workflow.associatedWith.map(({ project, issueTypes }) => ({
			project: { id: project.id },
			issueTypes: issueTypes.map((id) => id),
		})),
	};
};
